import { D365RequestError, TransientD365Error, type D365Client } from '@lg/d365-client';

/**
 * Browser side of the live slice: forwards an operation to the Cloudflare
 * Pages Function at /api/d365, which holds the D365 secret. Only the methods
 * of the live steps are ever called (see createHybridD365Client).
 */
export function createRemoteD365Client(endpoint = '/api/d365'): D365Client {
  const call = async (op: string, args: unknown[]): Promise<unknown> => {
    let res: Response;
    try {
      res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ op, args }),
      });
    } catch (err) {
      throw new TransientD365Error(err instanceof Error ? err.message : 'Network error', null, null);
    }
    const body = (await res.json().catch(() => null)) as {
      result?: unknown;
      detail?: string;
      title?: string;
    } | null;
    if (res.ok) return body?.result ?? null;
    const message = body?.detail ?? body?.title ?? `D365 proxy responded with HTTP ${res.status}`;
    if (res.status === 503) throw new TransientD365Error(message, 503, null);
    throw new D365RequestError(message, res.status);
  };
  return new Proxy({} as D365Client, {
    get: (_target, prop) => (typeof prop === 'string' ? (...args: unknown[]) => call(prop, args) : undefined),
  });
}

/** Steps the Pages Function can serve today; others stay simulated. */
export const SUPPORTED_LIVE_STEPS = [
  'createLegalEntity',
  'addressAndRegistration',
  'importPeople',
  'assignSecurity',
  'importCustomersVendors',
  'importProjects',
] as const;
